<script lang="ts">
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from "$lib/components/ui/dialog";
  import { Button } from "$lib/components/ui/button";
  import { Label } from "$lib/components/ui/label";
  import { Input } from "$lib/components/ui/input";
  import { Switch } from "$lib/components/ui/switch";
  import { t } from '$lib/i18n';
  import type { TranslationKey } from '$lib/i18n';
  import HelpButton from '../HelpButton.svelte';
  import { PhilatelicItemType } from '@briefmarken_katalog/types';
  import type { ReviewState } from '@briefmarken_katalog/types';
  import type { SearchFilter } from './SearchFilter';

  let {
    open = $bindable(),
    filter,
    showReviewStates = false,
    showOwnOnly = false,
    showReviewedByMe = false,
    onreset,
  } = $props<{
    open: boolean;
    filter: SearchFilter;
    showReviewStates?: boolean;
    showOwnOnly?: boolean;
    showReviewedByMe?: boolean;
    onreset: () => void;
  }>();

  const allItemTypes: { type: PhilatelicItemType; key: TranslationKey }[] = [
    { type: PhilatelicItemType.SingleStamp, key: 'search.singleStamp' },
    { type: PhilatelicItemType.Set,         key: 'menu.set' },
    { type: PhilatelicItemType.StampFromSet, key: 'search.stampFromSet' },
    { type: PhilatelicItemType.Block,        key: 'menu.block' },
    { type: PhilatelicItemType.Booklet,      key: 'menu.booklet' },
    { type: PhilatelicItemType.SeTenants,    key: 'menu.seTenants' },
  ];

  const allReviewStates: { state: ReviewState; key: TranslationKey }[] = [
    { state: 'Suggested', key: 'search.reviewStateSuggested' },
    { state: 'InReview',  key: 'search.reviewStateInReview' },
    { state: 'Accepted',  key: 'search.reviewStateAccepted' },
    { state: 'Rejected',  key: 'search.reviewStateRejected' },
  ];

  function toggleItemType(type: PhilatelicItemType, checked: boolean) {
    filter.itemTypes = checked
      ? [...filter.itemTypes, type]
      : filter.itemTypes.filter((t: PhilatelicItemType) => t !== type);
  }

  function toggleReviewState(state: ReviewState, checked: boolean) {
    filter.reviewStates = checked
      ? [...filter.reviewStates, state]
      : filter.reviewStates.filter((s: ReviewState) => s !== state);
  }
</script>

<Dialog bind:open>
  <DialogContent>
    <DialogHeader>
      <div class="flex items-center gap-2">
        <DialogTitle>{$t('search.title')}</DialogTitle>
        <HelpButton sectionId="search-filter" />
      </div>
      <DialogDescription>{$t('search.description')}</DialogDescription>
    </DialogHeader>

    <div class="grid gap-4 py-4">
      <!-- Enable / Disable -->
      <div class="flex items-center gap-3">
        <Switch bind:checked={filter.enabled} />
        <Label class="cursor-pointer select-none">
          {filter.enabled ? $t('search.enabled') : $t('search.disabled')}
        </Label>
      </div>

      <!-- Date range -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <Label for="date-from">{$t('search.dateFrom')}</Label>
          <Input id="date-from" type="date" bind:value={filter.dateFrom} disabled={!filter.enabled} />
        </div>
        <div class="flex flex-col gap-1">
          <Label for="date-to">{$t('search.dateTo')}</Label>
          <Input id="date-to" type="date" bind:value={filter.dateTo} disabled={!filter.enabled} />
        </div>
      </div>

      <!-- Suggested / reviewed by me -->
      {#if showOwnOnly || showReviewedByMe}
      <div class="flex gap-6">
        {#if showOwnOnly}
        <div class="flex items-center gap-2">
          <input
            id="own-only"
            type="checkbox"
            bind:checked={filter.ownOnly}
            disabled={!filter.enabled}
          />
          <Label for="own-only">{$t('search.ownOnly')}</Label>
        </div>
        {/if}
        {#if showReviewedByMe}
        <div class="flex items-center gap-2">
          <input
            id="reviewed-by-me"
            type="checkbox"
            bind:checked={filter.reviewedByMe}
            disabled={!filter.enabled}
          />
          <Label for="reviewed-by-me">{$t('search.reviewedByMe')}</Label>
        </div>
        {/if}
      </div>
      {/if}

      <!-- Description words -->
      <div class="flex flex-col gap-1">
        <Label for="desc-words">{$t('search.descriptionWords')}</Label>
        <Input
          id="desc-words"
          type="text"
          placeholder={$t('search.descriptionWordsPlaceholder')}
          bind:value={filter.descriptionWords}
          disabled={!filter.enabled}
        />
      </div>

      <!-- Zhihao -->
      <div class="flex flex-col gap-1">
        <Label for="zhihao-search">{$t('zhihao.button')}</Label>
        <Input
          id="zhihao-search"
          type="text"
          placeholder={$t('search.zhihaoPlaceholder')}
          bind:value={filter.zhihao}
          disabled={!filter.enabled}
        />
      </div>

      <!-- Item types -->
      <div class="flex flex-col gap-2">
        <Label>{$t('search.itemTypes')}</Label>
        <div class="grid grid-cols-3 gap-1">
          {#each allItemTypes as { type, key }}
          <div class="flex items-center gap-1">
            <input
              type="checkbox"
              checked={filter.itemTypes.includes(type)}
              onchange={(e) => toggleItemType(type, e.currentTarget.checked)}
              disabled={!filter.enabled}
            />
            <span class="text-sm">{$t(key)}</span>
          </div>
          {/each}
        </div>
      </div>

      <!-- Review states (suggestions dialog only) -->
      {#if showReviewStates}
      <div class="flex flex-col gap-2">
        <Label>{$t('search.reviewStates')}</Label>
        <div class="grid grid-cols-2 gap-1">
          {#each allReviewStates as { state, key }}
          <div class="flex items-center gap-1">
            <input
              type="checkbox"
              checked={filter.reviewStates.includes(state)}
              onchange={(e) => toggleReviewState(state, e.currentTarget.checked)}
              disabled={!filter.enabled}
            />
            <span class="text-sm">{$t(key)}</span>
          </div>
          {/each}
        </div>
      </div>
      {/if}
    </div>

    <DialogFooter>
      <Button variant="outline" onclick={onreset}>{$t('search.reset')}</Button>
    </DialogFooter>
  </DialogContent>
</Dialog>
